import {
  aggregateMissingMaterials,
  type MissingMaterialLine,
  type MissingMaterialSourceRow,
  type UnnamedMissingSku,
} from "./missingMaterialsReport";

function pdfEscape(value: string): string {
  return value
    .replace(/[^\x20-\x7E]/g, "?")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");
}

function clip(value: string, max: number): string {
  const text = value.replace(/\s+/g, " ").trim();
  return text.length <= max ? text : `${text.slice(0, Math.max(0, max - 3))}...`;
}

function qty(value: number): string {
  return String(value);
}

function materialLines(line: MissingMaterialLine): string[] {
  const head = [
    clip(line.material_code || "—", 16).padEnd(16),
    clip(line.name, 28).padEnd(28),
    clip(line.unit, 8).padEnd(8),
    qty(line.quantity).padStart(8),
    String(line.sku_count).padStart(6),
  ].join(" ");
  const wrapped: string[] = [];
  let current = "  SKUs";
  for (const code of line.sku_codes) {
    const next = `${current} ${code}`;
    if (next.length > 96) {
      wrapped.push(current);
      current = `       ${code}`;
    } else {
      current = next;
    }
  }
  if (current.trim()) wrapped.push(current);
  return [head, ...wrapped];
}

function reportLines(input: {
  scope: string;
  generatedOn: string;
  materials: MissingMaterialLine[];
  unnamed: UnnamedMissingSku[];
}): string[] {
  const skuCount = new Set(input.materials.flatMap((line) => line.sku_codes)).size;
  const lines = [
    "Missing materials",
    `${clip(input.scope, 40)}  ·  ${input.generatedOn}`,
    `${input.materials.length} materials on ${skuCount} product SKUs`,
    "Saved COGS summaries. Quantity is the sum of those bills.",
    "",
    `${"Code".padEnd(16)} ${"Name".padEnd(28)} ${"Unit".padEnd(8)} ${"Qty".padStart(8)} ${"SKUs".padStart(6)}`,
  ];
  if (input.materials.length === 0) {
    lines.push("", "No saved summary lists a material without a unit cost.");
  }
  for (const material of input.materials) lines.push(...materialLines(material), "");
  if (input.unnamed.length) {
    lines.push("Bills with more unpriced parts than the saved names");
    for (const row of input.unnamed) {
      lines.push(`${clip(row.code, 24).padEnd(24)} ${row.unnamed_count} unnamed`);
    }
  }
  return lines;
}

function pageStream(body: string[]): string {
  const commands = ["BT", "/F1 9 Tf", "36 740 Td", "12 TL"];
  for (const line of body) commands.push(`(${pdfEscape(line)}) Tj`, "T*");
  commands.push("ET");
  return commands.join("\n");
}

function buildPdf(streams: string[]): Uint8Array {
  const chunks: string[] = ["%PDF-1.4\n"];
  let length = chunks[0].length;
  const offsets: number[] = [];
  function add(id: number, body: string) {
    offsets[id] = length;
    const text = `${id} 0 obj\n${body}\nendobj\n`;
    chunks.push(text);
    length += text.length;
  }
  const fontId = 3;
  let next = 4;
  const pageIds: number[] = [];
  const contentIds: number[] = [];
  streams.forEach(() => {
    contentIds.push(next);
    pageIds.push(next + 1);
    next += 2;
  });
  add(1, "<< /Type /Catalog /Pages 2 0 R >>");
  add(2, `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageIds.length} >>`);
  add(fontId, "<< /Type /Font /Subtype /Type1 /BaseFont /Courier >>");
  streams.forEach((stream, index) => {
    add(contentIds[index], `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
    add(
      pageIds[index],
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents ${contentIds[index]} 0 R /Resources << /Font << /F1 ${fontId} 0 R >> >> >>`,
    );
  });
  const xrefAt = length;
  const size = next;
  let xref = `xref\n0 ${size}\n0000000000 65535 f \n`;
  for (let id = 1; id < size; id += 1) {
    xref += `${String(offsets[id] ?? 0).padStart(10, "0")} 00000 n \n`;
  }
  xref += `trailer\n<< /Size ${size} /Root 1 0 R >>\nstartxref\n${xrefAt}\n%%EOF`;
  chunks.push(xref);
  const bytes = new Uint8Array(length + xref.length);
  let offset = 0;
  for (const chunk of chunks) {
    for (let i = 0; i < chunk.length; i += 1) bytes[offset + i] = chunk.charCodeAt(i);
    offset += chunk.length;
  }
  return bytes;
}

const LINES_PER_PAGE = 52;

export function missingMaterialsReportPdf(input: {
  scope: string;
  generatedOn: string;
  materials: MissingMaterialLine[];
  unnamed: UnnamedMissingSku[];
}): Uint8Array {
  const lines = reportLines(input);
  const pages: string[][] = [];
  for (let i = 0; i < lines.length; i += LINES_PER_PAGE) pages.push(lines.slice(i, i + LINES_PER_PAGE));
  if (!pages.length) pages.push(["Missing materials"]);
  return buildPdf(pages.map(pageStream));
}

export function downloadMissingMaterialsPdf(input: {
  scope: string;
  generatedOn: string;
  rows: readonly MissingMaterialSourceRow[];
}) {
  const report = aggregateMissingMaterials(input.rows);
  const bytes = missingMaterialsReportPdf({
    scope: input.scope,
    generatedOn: input.generatedOn,
    materials: report.materials,
    unnamed: report.unnamed,
  });
  const blob = new Blob([bytes as unknown as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "missing-materials.pdf";
  a.click();
  URL.revokeObjectURL(url);
}
